import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Pause, Play, Download, Building2, Wand2, Trash2 } from "lucide-react";
import { DeleteEntitiesDialog, DeletedEntitiesPanel } from "./DeletedEntities";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";

const STATUS_TONE = {
  paid: "bg-emerald-500/10 text-emerald-700",
  trialing: "bg-blue-500/10 text-blue-700",
  complimentary: "bg-purple-500/10 text-purple-700",
  suspended: "bg-red-500/10 text-red-700",
  overdue: "bg-amber-500/10 text-amber-700",
  pending: "bg-slate-500/10 text-slate-600",
};

/**
 * Super-Admin Entities page — every registered org with plan/status +
 * bulk-select checkboxes for Deactivate / Reactivate / Export.
 */
export default function AdminEntities() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const load = useCallback(async () => {
    const r = await api.get("/admin/entities");
    setRows(r.data?.entities || []);
    setSel(new Set());
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter(e => {
    if (filter === "deactivated" ? !e.is_deactivated : (filter !== "all" && e.status !== filter)) return false;
    if (q && !`${e.name} ${e.gstin || ""} ${e.plan_id}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, q, filter]);

  const toggleOne = (id) => {
    const next = new Set(sel);
    next.has(id) ? next.delete(id) : next.add(id);
    setSel(next);
  };
  const toggleAll = () => {
    if (sel.size === filtered.length) setSel(new Set());
    else setSel(new Set(filtered.map(e => e.id)));
  };

  const bulk = async (action) => {
    if (sel.size === 0) return toast.error("Select at least one entity");
    if (action === "deactivate" && !confirm(`Deactivate ${sel.size} entities? Their users will be signed out immediately.`)) return;
    if (action === "reactivate" && !confirm(`Reactivate ${sel.size} entities?`)) return;
    setBusy(true);
    try {
      if (action === "export") {
        const resp = await api.post("/admin/entities/bulk-export",
          { org_ids: Array.from(sel) }, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a"); a.href = url;
        a.download = `bulk_export_${sel.size}_tenants.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${sel.size} tenant snapshots`);
      } else {
        const r = await api.post(`/admin/entities/bulk-${action}`, { org_ids: Array.from(sel) });
        toast.success(`${action === "deactivate" ? "Deactivated" : "Reactivated"} ${r.data.modified} of ${r.data.matched} entities`);
        await load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
    setBusy(false);
  };

  const allChecked = filtered.length > 0 && sel.size === filtered.length;
  const selectedRows = rows.filter((e) => sel.has(e.id));
  const allDeactivated = selectedRows.length > 0 && selectedRows.every((e) => e.is_deactivated);

  return (
    <div className="p-6 space-y-6" data-testid="admin-entities-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-500">// Entities</div>
          <h1 className="text-3xl font-bold mt-1 text-slate-900">Every company on the platform</h1>
          <div className="text-slate-500 text-sm mt-1">Multi-select rows to run bulk actions. Deactivation signs users out; data is preserved.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / GSTIN / plan…"
              className="h-9 rounded-sm border-slate-300 pl-8 text-sm w-64" data-testid="entities-search" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 rounded-sm text-sm w-40" data-testid="entities-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["all", "deactivated", "paid", "trialing", "complimentary", "suspended", "overdue", "pending"].map(s => (
                <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={async () => {
              if (!confirm("Run the legacy industry → business-type mapping now?\n\nThis assigns a founder-signed business_type to every org that doesn't have one. Existing values are left alone (use force=true from the API for a hard remap).")) return;
              setBusy(true);
              try {
                const pre = await api.post("/admin/business-types/migrate", { dry_run: true });
                if (pre.data.would_update === 0) {
                  toast.info("All tenants already carry a business_type — nothing to migrate.");
                } else if (!confirm(`Migrate ${pre.data.would_update} tenants?\n\nCounts:\n${Object.entries(pre.data.counts).filter(([,v]) => v>0).map(([k,v]) => `  ${k}: ${v}`).join("\n")}`)) {
                  return;
                } else {
                  const r = await api.post("/admin/business-types/migrate", {});
                  toast.success(`Migrated ${r.data.modified} tenants · ${r.data.skipped_already_matching} skipped`);
                  await load();
                }
              } catch (e) { toast.error(e?.response?.data?.detail || "Migration failed"); }
              finally { setBusy(false); }
            }} disabled={busy}
            className="rounded-sm gap-1.5" data-testid="bt-migrate">
            <Wand2 className="w-3.5 h-3.5" /> Migrate BT
          </Button>
        </div>
      </div>

      {sel.size > 0 && (
        <Card className="p-3 rounded-sm bg-orange-50 border-orange-200 flex items-center justify-between" data-testid="bulk-toolbar">
          <div className="text-sm text-orange-900 font-medium">
            {sel.size} of {filtered.length} entities selected
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => bulk("deactivate")} disabled={busy}
              className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 gap-1.5" data-testid="bulk-deactivate">
              <Pause className="w-3.5 h-3.5" /> Deactivate
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulk("reactivate")} disabled={busy}
              className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5" data-testid="bulk-reactivate">
              <Play className="w-3.5 h-3.5" /> Reactivate
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulk("export")} disabled={busy}
              className="rounded-sm gap-1.5" data-testid="bulk-export">
              <Download className="w-3.5 h-3.5" /> Export ZIP
            </Button>
            <Button size="sm" onClick={() => setDeleteOpen(true)} disabled={busy || !allDeactivated}
              title={allDeactivated ? "" : "Only deactivated companies can be deleted"}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="bulk-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
            {!allDeactivated && <span className="text-[11px] text-orange-800" data-testid="bulk-delete-hint">Delete works only on deactivated companies</span>}
          </div>
        </Card>
      )}

      <Card className="rounded-sm bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1050px]">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="p-3 w-10 text-center">
                  <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="bulk-check-all" />
                </th>
                <th className="text-left p-3">Entity</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">MRR (net)</th>
                <th className="text-right p-3">Users</th>
                <th className="text-left p-3">Trial ends</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-slate-500">
                  <Building2 className="w-8 h-8 mx-auto opacity-30" />
                  <div className="mt-2">No entities match.</div>
                </td></tr>
              )}
              {filtered.map(e => (
                <tr key={e.id} className={`border-b border-slate-100 hover:bg-slate-50 ${sel.has(e.id) ? "bg-orange-50/40" : ""}`}
                    data-testid={`entity-row-${e.id}`}>
                  <td className="p-3 text-center">
                    <Checkbox checked={sel.has(e.id)} onCheckedChange={() => toggleOne(e.id)} data-testid={`entity-check-${e.id}`} />
                  </td>
                  <td className="p-3">
                    <Link to={`/admin/tenants/${e.id}`} className="font-medium hover:text-orange-500">{e.name}</Link>
                    <div className="text-[10px] font-mono text-slate-500">{e.gstin || "no GSTIN"}</div>
                  </td>
                  <td className="p-3 uppercase font-mono text-[11px] text-slate-600">{e.plan_name || e.plan_id}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest ${STATUS_TONE[e.status] || STATUS_TONE.pending}`}>
                      {e.status}
                    </span>
                    {e.is_deactivated && <span className="ml-1 inline-block px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-red-100 text-red-700" data-testid={`entity-deactivated-${e.id}`}>deactivated</span>}
                  </td>
                  <td className="p-3 text-right font-mono">{fmtINR(e.mrr_net_inr)}</td>
                  <td className="p-3 text-right font-mono">{e.users_count || 0}</td>
                  <td className="p-3 text-[11px] text-slate-500">{e.trial_ends_at ? new Date(e.trial_ends_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-right">
                    <Link to={`/admin/tenants/${e.id}`} className="text-[11px] text-orange-500 hover:underline">Manage →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t border-slate-200 text-[11px] font-mono text-slate-500">
          {filtered.length} of {rows.length} entities
        </div>
      </Card>
      <DeletedEntitiesPanel refreshKey={refreshKey} onRestored={load} />
      <DeleteEntitiesDialog open={deleteOpen} onOpenChange={setDeleteOpen} orgs={selectedRows}
        onDone={() => { load(); setRefreshKey((k) => k + 1); }} />
    </div>
  );
}
